import {
  BunkerSigner,
  createNostrConnectURI,
  parseBunkerInput,
  type BunkerPointer,
  type ClientMetadata,
} from "nostr-tools/nip46"
import { generateSecretKey, getPublicKey } from "nostr-tools/pure"
import { bytesToHex, hexToBytes, randomHex } from "@/utils/hex"
import type { DraftEvent, NostrEvent } from "../types"
import { SignerError, type Signer } from "./types"

/** Everything needed to resume a NIP-46 pairing after a reload. */
export interface Nip46Connection {
  /** `remote-signer-pubkey` of the bunker (hex). */
  remoteSignerPubkey: string
  relays: string[]
  /** Single use secret from the `bunker://` token, if the signer provided one. */
  secret?: string | null
  /** Client key (hex). It can only be used through the bunker, never on its own. */
  clientSecretKey: string
}

/** A pending `nostrconnect://` pairing offered to a signer application. */
export interface NostrConnectOffer {
  uri: string
  secret: string
  /** Client key (hex) — keep it private, it is the pairing credential. */
  clientSecretKey: string
}

export interface Nip46SignerOptions {
  /** Reuse a saved client key instead of generating a new one. */
  clientSecretKey?: string
  /** How long a single RPC may take (default 60 s, the user approves on another device). */
  timeoutMs?: number
  /** Called when the signer requires an interactive approval URL. */
  onAuthUrl?: (url: string) => void
  clientMetadata?: ClientMetadata
}

export interface Nip46PairOptions extends Nip46SignerOptions {
  /** How long to wait for the signer application to answer the pairing URI. */
  pairTimeoutMs?: number
}

const DEFAULT_TIMEOUT = 60_000
const DEFAULT_PAIR_TIMEOUT = 180_000

/** Requests a new pairing URI carries; keep this list aligned with `auth.nip46Permissions`. */
export const DEFAULT_NIP46_PERMISSIONS = [
  "get_public_key",
  "sign_event:0",
  "sign_event:1",
  "sign_event:3",
  "sign_event:5",
  "sign_event:6",
  "sign_event:7",
  "sign_event:10000",
  "sign_event:10002",
  "sign_event:10003",
  "sign_event:10012",
  "sign_event:22242",
  "sign_event:30002",
  "sign_event:30023",
  "sign_event:31987",
  "nip04_encrypt",
  "nip04_decrypt",
]

function describeError(error: unknown): string {
  if (error instanceof SignerError) return error.message
  const raw = error instanceof Error ? error.message : String(error)
  return raw.replace(/^nostr-tools\/nip46:\s*/, "").trim() || "the remote signer did not answer"
}

function toSignerError(error: unknown, prefix: string, code: SignerError["code"] = "unavailable"): SignerError {
  if (error instanceof SignerError) return error
  return new SignerError(`${prefix}: ${describeError(error)}`, code)
}

/** Accepts a `bunker://` token or a `name@domain` NIP-05 pointer. */
export async function resolveBunkerPointer(input: string): Promise<BunkerPointer> {
  const trimmed = input.trim()
  if (!trimmed) throw new SignerError("Paste a bunker:// connection string first", "invalid")
  if (!/^bunker:\/\//i.test(trimmed) && !trimmed.includes("@")) {
    throw new SignerError("That is not a bunker:// connection string or a name@domain signer address", "invalid")
  }

  const pointer = await parseBunkerInput(trimmed).catch(() => null)
  if (!pointer) throw new SignerError("The signer could not be read from that address", "invalid")
  if (!/^[0-9a-f]{64}$/i.test(pointer.pubkey ?? "")) {
    throw new SignerError("The connection string does not contain a valid signer public key", "invalid")
  }
  if (!Array.isArray(pointer.relays) || pointer.relays.length === 0) {
    throw new SignerError("The connection string does not list a relay", "invalid")
  }
  return { ...pointer, pubkey: pointer.pubkey.toLowerCase() }
}

/** Builds the `nostrconnect://` URI a signer application scans or pastes. */
export function createNostrConnectOffer(options: {
  relays: string[]
  appName: string
  appUrl?: string
  permissions?: string[]
  clientSecretKey?: string
}): NostrConnectOffer {
  if (options.relays.length === 0) {
    throw new SignerError("No relay is configured for remote signer pairing", "unavailable")
  }
  const clientSecretKey = options.clientSecretKey ?? bytesToHex(generateSecretKey())
  const secret = randomHex(16)
  const uri = createNostrConnectURI({
    clientPubkey: getPublicKey(hexToBytes(clientSecretKey)),
    relays: options.relays,
    secret,
    perms: options.permissions && options.permissions.length > 0 ? options.permissions : undefined,
    name: options.appName,
    url: options.appUrl,
  })
  return { uri, secret, clientSecretKey }
}

/**
 * Remote signing through a NIP-46 bunker.
 *
 * The client key never leaves this instance: every request is encrypted for the
 * remote signer, which is the only party able to produce signatures.
 */
export class Nip46Signer implements Signer {
  readonly method = "nip46" as const
  private readonly bunker: BunkerSigner
  private readonly userPubkey: string
  private readonly clientSecretKey: string
  private closed = false

  private constructor(bunker: BunkerSigner, userPubkey: string, clientSecretKey: string) {
    this.bunker = bunker
    this.userPubkey = userPubkey
    this.clientSecretKey = clientSecretKey
  }

  private static params(options: Nip46SignerOptions) {
    return {
      timeout: options.timeoutMs ?? DEFAULT_TIMEOUT,
      onauth: options.onAuthUrl,
      // Relays are only switched when the signer asks for it on connect; doing it
      // on every restore adds a round trip for no benefit.
      skipSwitchRelays: true,
    }
  }

  private static async open(
    bunker: BunkerSigner,
    clientSecretKey: string,
    options: Nip46SignerOptions,
    connect: boolean,
  ): Promise<Nip46Signer> {
    try {
      if (connect) await bunker.connect(options.clientMetadata)
      const userPubkey = await bunker.getPublicKey()
      if (!/^[0-9a-f]{64}$/i.test(userPubkey)) {
        throw new SignerError("the remote signer returned an invalid public key", "invalid")
      }
      return new Nip46Signer(bunker, userPubkey.toLowerCase(), clientSecretKey)
    } catch (error) {
      await bunker.close().catch(() => undefined)
      throw toSignerError(
        error,
        connect ? "The remote signer refused the connection" : "Could not reach the remote signer",
      )
    }
  }

  /** Connects with a `bunker://` token (or a `name@domain` signer address). */
  static async fromBunkerUri(input: string, options: Nip46SignerOptions = {}): Promise<Nip46Signer> {
    const pointer = await resolveBunkerPointer(input)
    const clientSecretKey = options.clientSecretKey ?? bytesToHex(generateSecretKey())
    const bunker = BunkerSigner.fromBunker(hexToBytes(clientSecretKey), pointer, Nip46Signer.params(options))
    return Nip46Signer.open(bunker, clientSecretKey, options, true)
  }

  /** Reuses a saved pairing; asks the bunker for its user key to validate the session. */
  static async fromConnection(
    connection: Nip46Connection,
    options: Nip46SignerOptions = {},
  ): Promise<Nip46Signer> {
    const pointer: BunkerPointer = {
      pubkey: connection.remoteSignerPubkey,
      relays: [...connection.relays],
      secret: connection.secret ?? null,
    }
    const bunker = BunkerSigner.fromBunker(
      hexToBytes(connection.clientSecretKey),
      pointer,
      Nip46Signer.params(options),
    )
    return Nip46Signer.open(bunker, connection.clientSecretKey, options, false)
  }

  /** Waits for a signer application to answer a `nostrconnect://` URI. */
  static async pair(offer: NostrConnectOffer, options: Nip46PairOptions = {}): Promise<Nip46Signer> {
    let bunker: BunkerSigner
    try {
      bunker = await BunkerSigner.fromURI(
        hexToBytes(offer.clientSecretKey),
        offer.uri,
        Nip46Signer.params(options),
        options.pairTimeoutMs ?? DEFAULT_PAIR_TIMEOUT,
      )
    } catch (error) {
      throw toSignerError(error, "Pairing with the signer failed")
    }
    return Nip46Signer.open(bunker, offer.clientSecretKey, options, false)
  }

  get publicKey(): string {
    return this.userPubkey
  }

  getConnection(): Nip46Connection {
    return {
      remoteSignerPubkey: this.bunker.bp.pubkey,
      relays: [...this.bunker.bp.relays],
      secret: this.bunker.bp.secret ?? null,
      clientSecretKey: this.clientSecretKey,
    }
  }

  async getPublicKey(): Promise<string> {
    return this.userPubkey
  }

  async signEvent(draft: DraftEvent): Promise<NostrEvent> {
    this.assertOpen()
    try {
      const signed = await this.bunker.signEvent({
        kind: draft.kind,
        content: draft.content,
        tags: draft.tags,
        created_at: draft.created_at,
      })
      return signed as NostrEvent
    } catch (error) {
      throw toSignerError(error, "The remote signer could not sign this event", "rejected")
    }
  }

  async nip04Encrypt(pubkey: string, plaintext: string): Promise<string> {
    this.assertOpen()
    try {
      return await this.bunker.nip04Encrypt(pubkey, plaintext)
    } catch (error) {
      throw toSignerError(error, "The remote signer could not encrypt the message", "rejected")
    }
  }

  async nip04Decrypt(pubkey: string, ciphertext: string): Promise<string> {
    this.assertOpen()
    try {
      return await this.bunker.nip04Decrypt(pubkey, ciphertext)
    } catch (error) {
      throw toSignerError(error, "The remote signer could not decrypt the message", "rejected")
    }
  }

  async close(): Promise<void> {
    if (this.closed) return
    this.closed = true
    await this.bunker.close().catch(() => undefined)
  }

  private assertOpen() {
    if (this.closed) throw new SignerError("this remote signer session was closed", "unavailable")
  }
}
